/**
 * MeetingTimeline — barra horizontal con secciones coloreadas (Fase 50).
 *
 * Cada sección ocupa un ancho proporcional a su duración.
 * Click → selecciona. Doble clic → reproduce 10s de audio.
 */

import { useRef, useState, useCallback } from "react";
import styled from "styled-components";
import type { MeetingSection } from "../../lib/types.ts";
import type { SectionEdit } from "../../state/atoms.ts";

// ---------------------------------------------------------------------------
// Colores por tipo de sección
// ---------------------------------------------------------------------------

export const SECTION_TYPE_COLORS: Record<string, string> = {
  intro: "#6366f1",
  as_is: "#0ea5e9",
  to_be: "#8b5cf6",
  discussion: "#f59e0b",
  decisions: "#16a34a",
  tasks: "#dc2626",
  closing: "#6b7280",
  other: "#9ca3af",
};

const IRRELEVANT_COLOR = "#9ca3af";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Bar = styled.div`
  display: flex;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  gap: 1px;
  position: relative;
  user-select: none;
`;

const Block = styled.div<{
  $width: number;
  $color: string;
  $isSelected: boolean;
  $isPlaying: boolean;
  $isIrrelevant: boolean;
}>`
  width: ${({ $width }) => $width}%;
  min-width: 2%;
  background: ${({ $isIrrelevant, $color }) =>
    $isIrrelevant ? IRRELEVANT_COLOR : $color};
  opacity: ${({ $isIrrelevant }) => ($isIrrelevant ? 0.6 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: opacity 0.15s;
  border-radius: 2px;
  outline: ${({ $isSelected, theme }) =>
    $isSelected ? `2px solid ${theme.color.accent}` : "none"};
  outline-offset: -2px;

  &:hover {
    opacity: ${({ $isIrrelevant }) => ($isIrrelevant ? 0.75 : 0.85)};
  }
`;

const BlockTitle = styled.span`
  font-size: 10px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
  font-weight: 500;
  line-height: 1;
`;

const ProgressOverlay = styled.div<{ $progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: ${({ $progress }) => $progress * 100}%;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0 2px 2px 0;
  transition: width 50ms linear;
`;

const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 6px 10px;
  font-size: ${({ theme }) => theme.font.size.sm};
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: ${({ $visible }) => ($visible ? "block" : "none")};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeTooltip(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type MeetingTimelineProps = {
  sections: MeetingSection[];
  totalStart: number;
  totalEnd: number;
  selectedId: string | null;
  playingId: string | null;
  progress: number;
  edits: Map<string, SectionEdit>;
  onSelect: (id: string) => void;
  onPlay: (id: string, startTime: number) => void;
};

export function MeetingTimeline({
  sections,
  totalStart,
  totalEnd,
  selectedId,
  playingId,
  progress,
  edits,
  onSelect,
  onPlay,
}: MeetingTimelineProps) {
  const totalDuration = totalEnd - totalStart || 1;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const lastClickRef = useRef<{ id: string; time: number }>({ id: "", time: 0 });

  const handleClick = useCallback(
    (section: MeetingSection) => {
      const now = Date.now();
      const prev = lastClickRef.current;

      if (prev.id === section.id && now - prev.time < 400) {
        // Doble clic → reproducir
        onPlay(section.id, section.start);
        prev.id = "";
        prev.time = 0;
      } else {
        onSelect(section.id);
        prev.id = section.id;
        prev.time = now;
      }
    },
    [onSelect, onPlay],
  );

  if (sections.length === 0) return null;

  return (
    <Bar>
      {sections.map((section) => {
        const edit = edits.get(section.id);
        const title = edit?.title ?? section.title;
        const isRelevant =
          edit?.isRelevant !== undefined
            ? edit.isRelevant
            : section.isRelevant;
        const width = Math.max(
          ((section.end - section.start) / totalDuration) * 100,
          2,
        );
        const color =
          SECTION_TYPE_COLORS[section.type] ?? SECTION_TYPE_COLORS.other;
        const isSelected = selectedId === section.id;
        const isPlaying = playingId === section.id;

        return (
          <Block
            key={section.id}
            $width={width}
            $color={color}
            $isSelected={isSelected}
            $isPlaying={isPlaying}
            $isIrrelevant={!isRelevant}
            onClick={() => handleClick(section)}
            onMouseEnter={() => setHoveredId(section.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <BlockTitle>{title}</BlockTitle>
            {isPlaying && <ProgressOverlay $progress={progress} />}
            <Tooltip $visible={hoveredId === section.id}>
              {title} — {formatTimeTooltip(section.start)} →{" "}
              {formatTimeTooltip(section.end)}
              {!isRelevant && " (no relevante)"}
            </Tooltip>
          </Block>
        );
      })}
    </Bar>
  );
}
